// /discover — public player discovery page (M5 Phase 3).
//
// Single source of truth for filter state is the URL query string, so every
// view is shareable: a coach can paste a link like `/discover?position=PG&
// nationality=VNM&verified_only=1` and land on the same filtered grid.
//
// Layout:
//   - Top: search input + sort dropdown + result count
//   - Sidebar (md+): filter controls. On mobile, collapses behind a toggle.
//   - Main: player card grid (responsive 1-2-3 cols).
//   - Bottom: Load more button (cursorless offset pagination).
//
// Recency tags are computed client-side from created_at/updated_at so the
// API response stays slim and the labels stay fresh between renders.

import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Bookmark, ChevronDown, Lock, SlidersHorizontal, Sparkles } from 'lucide-react'
import { useSession } from '../lib/auth'
import { apiJson } from '../lib/api'
import type {
  AvailabilityStatus,
  LeagueTier,
  PlayerPosition,
} from '../types'
import PlayerCard, { type PlayerSearchResult } from '../components/PlayerCard'
import { PlayerCardGridSkeleton } from '../components/Skeleton'
import { disambiguateLeagueName } from './Browse'

interface SearchResponse {
  results: PlayerSearchResult[]
  has_more: boolean
}

type OrderBy = 'recent' | 'verified' | 'relevant'

const POSITIONS: PlayerPosition[] = ['PG', 'SG', 'SF', 'PF', 'C']
const AVAILABILITY: AvailabilityStatus[] = ['open', 'evaluating', 'closed']
const AVAILABILITY_LABEL: Record<AvailabilityStatus, string> = {
  open: 'Open to offers',
  evaluating: 'Evaluating',
  closed: 'Not available',
}
const TIERS: LeagueTier[] = ['T1', 'T2', 'T3']
const TIER_LABEL: Record<LeagueTier, string> = {
  T1: 'T1 — top target',
  T2: 'T2 — strong fit',
  T3: 'T3 — backup',
}

// M5.2 — provenance filter. Mirrors the players.source enum. The empty
// string means "no filter" — Discover's default dropdown value.
type SourceFilter = '' | 'agent' | 'api_sports'
const SOURCE_OPTIONS: Array<{ value: SourceFilter; label: string }> = [
  { value: '', label: 'All players' },
  { value: 'agent', label: 'With agent representation' },
  { value: 'api_sports', label: 'Discovery (no agent yet)' },
]

const PAGE_SIZE = 20

// --- NL search (2026-07-30D) ------------------------------------------
// Friendly labels for the params the AI translator can apply, so the coach
// sees "Min PPG 15 · Position PG", never raw query-string keys. Keys missing
// from this map fall back to the raw key — new params degrade readable.
const AI_PARAM_LABELS: Record<string, string> = {
  q: 'Text',
  position: 'Position',
  nationality: 'Nationality',
  league: 'League',
  league_tier: 'League tier',
  availability_status: 'Availability',
  min_height_cm: 'Min height (cm)',
  max_height_cm: 'Max height (cm)',
  min_age: 'Min age',
  max_age: 'Max age',
  verified_only: 'Verified only',
  is_3x3: '3x3 active',
  fiba_only: 'FIBA-licensed agent',
  source: 'Source',
  order_by: 'Sort',
  min_ppg: 'Min PPG',
  min_rpg: 'Min RPG',
  min_apg: 'Min APG',
  min_mpg: 'Min MPG',
  min_fg_pct: 'Min FG%',
  min_3p_pct: 'Min 3PT%',
  min_ft_pct: 'Min FT%',
}

interface AiSearchResponse {
  error?: string
  message?: string
  params_used?: Record<string, string>
  dropped_keys?: string[]
  note?: string
  can_search?: boolean
}

// The visible receipt for the last translation. The coach MUST see which
// filters were applied — a black box that silently drops half the request is
// worse than no feature — so this persists under the input until the next
// submit or a manual dismiss.
interface AiFeedback {
  kind: 'applied' | 'noop' | 'error'
  message: string
  applied?: Array<{ key: string; value: string }>
  dropped?: string[]
}

// Stat-threshold filters (2026-07-25) — min-only, matched server-side
// against each player's most recent non-3x3 season. URL param names are
// the API's; labels are the coach-facing stat names.
const STAT_FILTER_DEFS = [
  { key: 'min_ppg', label: 'PPG' },
  { key: 'min_rpg', label: 'RPG' },
  { key: 'min_apg', label: 'APG' },
  { key: 'min_mpg', label: 'MPG' },
  { key: 'min_fg_pct', label: 'FG%' },
  { key: 'min_3p_pct', label: '3PT%' },
  { key: 'min_ft_pct', label: 'FT%' },
] as const
type StatFilterKey = (typeof STAT_FILTER_DEFS)[number]['key']

// Map sticky URL params into a typed snapshot. Anything not present stays
// null/empty so the UI can show "Any". Numeric inputs round-trip as strings.
function readParams(sp: URLSearchParams) {
  return {
    q: sp.get('q') ?? '',
    position: (sp.get('position') ?? '') as PlayerPosition | '',
    nationality: sp.get('nationality') ?? '',
    // M6.2 — league slug from /api/players/leagues. Unknown slug
    // collapses to an empty result on the server, no special UI
    // handling required.
    league: sp.get('league') ?? '',
    league_tier: (sp.get('league_tier') ?? '') as LeagueTier | '',
    availability_status: (sp.get('availability_status') ?? '') as AvailabilityStatus | '',
    min_height_cm: sp.get('min_height_cm') ?? '',
    max_height_cm: sp.get('max_height_cm') ?? '',
    min_age: sp.get('min_age') ?? '',
    max_age: sp.get('max_age') ?? '',
    verified_only: sp.get('verified_only') === '1',
    // Tier-1 content batch — manual 3x3-active tag filter, chip in the
    // Position block. Same boolean round-trip shape as verified_only.
    is_3x3: sp.get('is_3x3') === '1',
    fiba_only: sp.get('fiba_only') === '1',
    // Stat thresholds — string round-trip like the height/age numerics.
    min_ppg: sp.get('min_ppg') ?? '',
    min_rpg: sp.get('min_rpg') ?? '',
    min_apg: sp.get('min_apg') ?? '',
    min_mpg: sp.get('min_mpg') ?? '',
    min_fg_pct: sp.get('min_fg_pct') ?? '',
    min_3p_pct: sp.get('min_3p_pct') ?? '',
    min_ft_pct: sp.get('min_ft_pct') ?? '',
    order_by: (sp.get('order_by') ?? 'recent') as OrderBy,
    // M5.2 — source filter from URL. Unknown values collapse to '' so
    // a hand-edited link with `?source=garbage` shows the unfiltered grid
    // rather than triggering an endless 400 from the API.
    source: (sp.get('source') === 'agent' || sp.get('source') === 'api_sports'
      ? (sp.get('source') as 'agent' | 'api_sports')
      : '') as SourceFilter,
  }
}

type FilterState = ReturnType<typeof readParams>

function buildSearchUrl(filters: FilterState, offset: number): string {
  const out = new URLSearchParams()
  if (filters.q) out.set('q', filters.q)
  if (filters.position) out.set('position', filters.position)
  if (filters.nationality) out.set('nationality', filters.nationality)
  if (filters.league) out.set('league', filters.league)
  if (filters.league_tier) out.set('league_tier', filters.league_tier)
  if (filters.availability_status) {
    out.set('availability_status', filters.availability_status)
  }
  if (filters.min_height_cm) out.set('min_height_cm', filters.min_height_cm)
  if (filters.max_height_cm) out.set('max_height_cm', filters.max_height_cm)
  if (filters.min_age) out.set('min_age', filters.min_age)
  if (filters.max_age) out.set('max_age', filters.max_age)
  if (filters.verified_only) out.set('verified_only', '1')
  if (filters.is_3x3) out.set('is_3x3', '1')
  if (filters.fiba_only) out.set('fiba_only', '1')
  for (const def of STAT_FILTER_DEFS) {
    if (filters[def.key]) out.set(def.key, filters[def.key])
  }
  if (filters.source) out.set('source', filters.source)
  out.set('order_by', filters.order_by)
  out.set('limit', String(PAGE_SIZE))
  out.set('offset', String(offset))
  return `/api/players/search?${out.toString()}`
}

// NL search input (2026-07-30D). Coach types a sentence; POST /api/ai/search
// translates it into existing search params; we write those into the URL —
// the single source of truth — and the page's normal fetch effect runs the
// real search exactly as if the coach had set each filter by hand. No second
// results pipeline, no duplicated state.
//
// Signed-out users see the input (the feature should be discoverable) and get
// a sign-in nudge from the 401 instead of a dead control.
function AiSearchBar({
  onApply,
}: {
  onApply: (params: Record<string, string>) => void
}) {
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState<AiFeedback | null>(null)

  async function submit() {
    const trimmed = query.trim()
    if (!trimmed || busy) return
    setBusy(true)
    setFeedback(null)
    try {
      const res = await fetch('/api/ai/search', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: trimmed }),
      })
      const data = (await res.json().catch(() => null)) as AiSearchResponse | null
      if (res.status === 401) {
        setFeedback({ kind: 'error', message: 'Sign in to use AI search.' })
        return
      }
      if (res.status === 429) {
        setFeedback({
          kind: 'error',
          message: data?.message ?? 'Daily AI search limit reached — back tomorrow.',
        })
        return
      }
      if (!res.ok || !data) {
        setFeedback({
          kind: 'error',
          message: data?.message ?? 'AI search is unavailable right now. The filters on the left still work.',
        })
        return
      }
      const applied = Object.entries(data.params_used ?? {}).map(([key, value]) => ({
        key,
        value,
      }))
      if (applied.length === 0) {
        // Out of scope, or only asked for data that does not exist. Leave the
        // current filters alone and surface the model's explanation.
        setFeedback({
          kind: 'noop',
          message:
            data.note || 'I can only search players right now — try describing position, nationality, height, age, or per-game stats.',
        })
        return
      }
      setFeedback({
        kind: 'applied',
        message: data.note ?? '',
        applied,
        dropped: data.dropped_keys ?? [],
      })
      onApply(data.params_used ?? {})
    } catch {
      setFeedback({
        kind: 'error',
        message: 'AI search is unavailable right now. The filters on the left still work.',
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mb-5">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void submit()
        }}
        className="flex items-center gap-2 bg-white dark:bg-hoop-surface border border-hoop-border rounded-lg px-3.5 py-2.5 focus-within:border-hoop-accent transition-colors"
      >
        <Sparkles className="w-4 h-4 text-hoop-accent shrink-0" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Describe the player you're looking for"
          placeholder="Describe the player you're looking for — e.g. a tall Vietnamese point guard who scores over 15 a game"
          maxLength={400}
          className="flex-1 bg-transparent text-[13.5px] text-hoop-text placeholder:text-hoop-text-3 outline-none min-w-0"
        />
        <button
          type="submit"
          disabled={busy || !query.trim()}
          className="shrink-0 bg-hoop-primary text-hoop-primary-fg rounded-md px-3 py-1.5 text-[12.5px] font-medium hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {busy ? 'Translating…' : 'Search'}
        </button>
      </form>

      {feedback && (
        <div
          className={`mt-2 rounded-md border px-3 py-2 text-[12.5px] ${
            feedback.kind === 'error'
              ? 'border-hoop-red-bg bg-hoop-red-bg text-hoop-red'
              : 'border-hoop-border bg-hoop-bg text-hoop-text-2'
          }`}
        >
          {feedback.kind === 'applied' && feedback.applied && (
            <span className="flex flex-wrap items-center gap-1.5">
              <span className="font-medium text-hoop-text">Understood:</span>
              {feedback.applied.map(({ key, value }) => (
                <span
                  key={key}
                  className="inline-flex items-center rounded-full border border-hoop-border bg-hoop-surface px-2 py-0.5 text-[11.5px] text-hoop-text"
                >
                  {AI_PARAM_LABELS[key] ?? key}
                  {value !== '1' && `: ${value}`}
                </span>
              ))}
            </span>
          )}
          {feedback.message && (
            <p className={feedback.kind === 'applied' ? 'mt-1' : ''}>{feedback.message}</p>
          )}
          {feedback.kind === 'applied' && (feedback.dropped?.length ?? 0) > 0 && (
            <p className="mt-1 text-hoop-text-3">
              Ignored (not searchable here): {feedback.dropped!.join(', ')}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default function Discover() {
  const [searchParams, setSearchParams] = useSearchParams()
  const filters = useMemo(() => readParams(searchParams), [searchParams])
  const { data: session } = useSession()
  // Saved searches (migration 0019) — viewer gate for the Save-search
  // affordance. Pro coach → live button; free coach → locked teaser
  // linking /pricing; agents/anon → hidden entirely. COSMETIC only: the
  // server re-gates every saved-searches call via billing.ts.
  const [viewerCoachTier, setViewerCoachTier] = useState<'free' | 'pro' | null>(null)
  const [saveOpen, setSaveOpen] = useState(false)
  const [saveName, setSaveName] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [saveOk, setSaveOk] = useState<string | null>(null)

  useEffect(() => {
    if (!session?.user) {
      setViewerCoachTier(null)
      return
    }
    let cancelled = false
    apiJson<{ profile: { role: string | null; tier: 'free' | 'pro' } }>('/api/me')
      .then((me) => {
        if (cancelled) return
        setViewerCoachTier(me.profile.role === 'coach' ? me.profile.tier : null)
      })
      .catch(() => {
        if (!cancelled) setViewerCoachTier(null)
      })
    return () => {
      cancelled = true
    }
  }, [session])

  async function submitSaveSearch() {
    const name = saveName.trim()
    if (!name || saving) return
    setSaving(true)
    setSaveErr(null)
    try {
      await apiJson('/api/saved-searches', {
        method: 'POST',
        body: JSON.stringify({ name, params: searchParams.toString() }),
      })
      setSaveOpen(false)
      setSaveName('')
      setSaveOk(name)
      window.setTimeout(() => setSaveOk(null), 3000)
    } catch (e) {
      const key = e instanceof Error ? e.message : 'save_failed'
      setSaveErr(
        key === 'duplicate_name'
          ? 'You already have a saved search with this name.'
          : key === 'saved_search_cap'
            ? 'Saved-search limit reached (20). Delete one from your dashboard first.'
            : key === 'name_too_long'
              ? 'Name is too long (60 characters max).'
              : key === 'pro_required'
                ? 'Saved searches are a Coach Pro feature.'
                : key,
      )
    } finally {
      setSaving(false)
    }
  }

  const [results, setResults] = useState<PlayerSearchResult[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  // Local mirror of the q text field so typing stays instant while the URL
  // (and the fetch it drives) updates on a debounce — see the effect below.
  // Initialized from the URL so shared links like ?q=… still populate it.
  const [qInput, setQInput] = useState(filters.q)
  // M5.2 + M5.3 — populated once from /api/players/countries. M5.3
  // widened the response shape to { slug, name, player_count } per
  // entry; Discover only needs the name for the dropdown, so we
  // project down at load time.
  const [countries, setCountries] = useState<string[]>([])
  // M6.2 — leagues directory for the league dropdown. Keeps slug +
  // name + country + player_count: name is the base label, country
  // disambiguates colliding names (M6.2-followup), player_count
  // shows in parens after the label, slug is the URL value.
  const [leagues, setLeagues] = useState<
    Array<{
      slug: string
      name: string
      country: string | null
      player_count: number
    }>
  >([])

  // Lazy load the country list. Failures are silent — the dropdown
  // falls back to an empty list, which the UI handles by showing only
  // the "Any" option (still usable for clearing the filter).
  useEffect(() => {
    let cancelled = false
    fetch('/api/players/countries')
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (data: { countries?: Array<{ name?: string }> } | null) => {
          if (cancelled) return
          const list = Array.isArray(data?.countries) ? data.countries : []
          setCountries(
            list
              .map((c) => (typeof c?.name === 'string' ? c.name : null))
              .filter((s): s is string => s !== null),
          )
        },
      )
      .catch(() => {
        /* keep countries empty on failure */
      })
    return () => {
      cancelled = true
    }
  }, [])

  // M6.2 — same lazy-load pattern for /api/players/leagues. Failure
  // is silent for the same reasons: an empty dropdown still works
  // for clearing the filter via the "Any" option.
  useEffect(() => {
    let cancelled = false
    fetch('/api/players/leagues')
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (data: {
          leagues?: Array<{
            slug?: string
            name?: string
            country?: string | null
            player_count?: number
          }>
        } | null) => {
          if (cancelled) return
          const list = Array.isArray(data?.leagues) ? data.leagues : []
          setLeagues(
            list
              .filter(
                (l): l is {
                  slug: string
                  name: string
                  country: string | null
                  player_count: number
                } =>
                  typeof l?.slug === 'string' &&
                  typeof l?.name === 'string' &&
                  typeof l?.player_count === 'number',
              )
              .map((l) => ({
                slug: l.slug,
                name: l.name,
                // Defensive normalization — country may be omitted on
                // older API responses. Treat undefined the same as null.
                country: typeof l.country === 'string' ? l.country : null,
                player_count: l.player_count,
              })),
          )
        },
      )
      .catch(() => {
        /* keep leagues empty on failure */
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Keep the q box in sync when q changes from outside the text input —
  // Clear all, back/forward nav, a shared link. Only the q field is mirrored;
  // the discrete filter controls read straight from the URL.
  useEffect(() => {
    setQInput(filters.q)
  }, [filters.q])

  // Debounce the q → URL write (300ms) with replace:true so a burst of
  // keystrokes collapses into a single history entry + a single fetch instead
  // of one of each per character. No-op when the value already matches the
  // URL, which also prevents a spurious history push on mount.
  useEffect(() => {
    if (qInput === filters.q) return
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (qInput) next.set('q', qInput)
      else next.delete('q')
      setSearchParams(next, { replace: true })
    }, 300)
    return () => clearTimeout(t)
  }, [qInput, filters.q, searchParams, setSearchParams])

  // Initial fetch + refetch when filters change. We always reset to offset=0
  // when filters change.
  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setErr(null)
    setOffset(0)
    fetch(buildSearchUrl(filters, 0))
      .then(async (res) => {
        if (cancelled) return
        if (!res.ok) {
          const body = await res.json().catch(() => null) as { error?: string } | null
          throw new Error(body?.error ?? `request_failed_${res.status}`)
        }
        const data = (await res.json()) as SearchResponse
        if (cancelled) return
        setResults(data.results)
        setHasMore(data.has_more)
      })
      .catch((e: unknown) => {
        if (!cancelled) setErr(e instanceof Error ? e.message : 'unknown_error')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filters])

  async function loadMore() {
    if (loadingMore || !hasMore) return
    setLoadingMore(true)
    setErr(null)
    const nextOffset = offset + PAGE_SIZE
    try {
      const res = await fetch(buildSearchUrl(filters, nextOffset))
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { error?: string } | null
        throw new Error(body?.error ?? `request_failed_${res.status}`)
      }
      const data = (await res.json()) as SearchResponse
      setResults((prev) => [...prev, ...data.results])
      setHasMore(data.has_more)
      setOffset(nextOffset)
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : 'unknown_error')
    } finally {
      setLoadingMore(false)
    }
  }

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K]) {
    const next = new URLSearchParams(searchParams)
    if (value === '' || value === false) {
      next.delete(key)
    } else if (value === true) {
      next.set(key, '1')
    } else {
      next.set(key, String(value))
    }
    setSearchParams(next, { replace: false })
  }

  function clearAll() {
    setSearchParams(new URLSearchParams(), { replace: false })
  }

  // Both height keys in ONE setSearchParams call. Calling updateFilter twice
  // would not work: each call rebuilds `next` from the same render's
  // `searchParams`, so the second write clobbers the first and one of the two
  // keys survives.
  function clearHeightFilters() {
    const next = new URLSearchParams(searchParams)
    next.delete('min_height_cm')
    next.delete('max_height_cm')
    setSearchParams(next, { replace: false })
  }

  // Same one-call rule as clearHeightFilters above. The Age chip had the
  // identical two-sequential-updateFilter defect: clicking remove left
  // min_age behind in the URL.
  function clearAgeFilters() {
    const next = new URLSearchParams(searchParams)
    next.delete('min_age')
    next.delete('max_age')
    setSearchParams(next, { replace: false })
  }

  const hasActiveFilters =
    !!filters.q ||
    !!filters.position ||
    !!filters.nationality ||
    !!filters.league ||
    !!filters.league_tier ||
    !!filters.availability_status ||
    !!filters.min_height_cm ||
    !!filters.max_height_cm ||
    !!filters.min_age ||
    !!filters.max_age ||
    filters.verified_only ||
    filters.is_3x3 ||
    filters.fiba_only ||
    STAT_FILTER_DEFS.some((def) => !!filters[def.key]) ||
    !!filters.source

  const activeFilterCount = [
    filters.q,
    filters.position,
    filters.nationality,
    filters.league,
    filters.league_tier,
    filters.availability_status,
    filters.min_height_cm,
    filters.max_height_cm,
    filters.min_age,
    filters.max_age,
    filters.verified_only,
    filters.is_3x3,
    filters.fiba_only,
    ...STAT_FILTER_DEFS.map((def) => filters[def.key]),
    filters.source,
  ].filter(Boolean).length

  // Height filters compare against player_measurements.height_cm, and a SQL
  // comparison against NULL is never true — so any height bound silently drops
  // every player with no recorded height. That exclusion is CORRECT (a coach
  // asking for 190cm+ does not want unmeasured players mixed in), but it is
  // invisible, which makes a thin result set read as a broken filter. Both the
  // empty state and the result-count caption below exist to make it legible.
  const heightFilterActive = !!filters.min_height_cm || !!filters.max_height_cm
  // min and max each count as their own entry in activeFilterCount, so both
  // have to come off before asking whether anything else is still narrowing.
  const heightFilterCount =
    (filters.min_height_cm ? 1 : 0) + (filters.max_height_cm ? 1 : 0)
  const hasOtherThanHeight = activeFilterCount - heightFilterCount > 0

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* UIR-1 header row: larger title + cleaner subtitle + search/sort controls */}
      <header className="mb-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
          <div>
            <h1 className="font-display text-[26px] font-semibold tracking-tight text-hoop-text">
              Discover players
            </h1>
            <p className="text-sm text-hoop-text-2 mt-1">
              Verified, agent-managed profiles across international leagues.
            </p>
          </div>
          {/* flex-wrap + min-w-0: at 360px the search box takes the full first
              row and the sort/save controls wrap beneath it. Without min-w-0
              the input's intrinsic min-content width forces the row past the
              viewport (body pans). Widths stay on the wrapper per #38. */}
          <div className="flex flex-wrap gap-2.5">
            <div className="flex items-center gap-2 bg-white dark:bg-hoop-surface border border-hoop-border rounded-lg px-3.5 py-2 w-full md:w-[280px]">
              <span className="text-hoop-text-3 text-sm">⌕</span>
              <input
                type="search"
                aria-label="Search players by name"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder="Search by name…"
                className="flex-1 min-w-0 bg-transparent text-[13.5px] text-hoop-text placeholder:text-hoop-text-3 outline-none"
              />
            </div>
            <div className="flex items-center gap-2 bg-white dark:bg-hoop-surface border border-hoop-border rounded-lg px-3.5 py-2">
              <select
                aria-label="Sort results"
                value={filters.order_by}
                onChange={(e) => updateFilter('order_by', e.target.value as OrderBy)}
                className="bg-transparent text-[13.5px] text-hoop-text outline-none cursor-pointer appearance-none pr-5"
                style={{
                  backgroundImage:
                    'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 12 12\'%3E%3Cpath fill=\'%23A0A29C\' d=\'M6 8l-4-4h8z\'/%3E%3C/svg%3E")',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right center',
                }}
              >
                <option value="recent">Most recent</option>
                <option value="verified">Verified first</option>
                <option value="relevant">Most relevant</option>
              </select>
            </div>
            {/* Saved searches — Pro coach: live save button; free coach:
                locked teaser to /pricing; everyone else: nothing. */}
            {viewerCoachTier === 'pro' && (
              <button
                type="button"
                onClick={() => {
                  setSaveErr(null)
                  setSaveOpen(true)
                }}
                className="flex items-center gap-1.5 bg-hoop-primary text-hoop-primary-fg rounded-lg px-3.5 py-2 text-[13.5px] font-medium hover:opacity-90 whitespace-nowrap"
              >
                <Bookmark className="w-4 h-4" />
                Save search
              </button>
            )}
            {viewerCoachTier === 'free' && (
              <Link
                to="/pricing"
                title="Available with Coach Pro"
                className="flex items-center gap-1.5 bg-white dark:bg-hoop-surface border border-hoop-border rounded-lg px-3.5 py-2 text-[13.5px] text-hoop-text-3 whitespace-nowrap hover:border-hoop-accent"
              >
                <Lock className="w-3.5 h-3.5" />
                Save search
                <span className="text-[11px] font-medium text-hoop-accent">
                  Coach Pro
                </span>
              </Link>
            )}
          </div>
        </div>
        {saveOk && (
          <p className="text-sm text-hoop-green mb-2">
            Saved "{saveOk}" — find it on your dashboard.
          </p>
        )}
        {/* Mobile filter toggle */}
        <button
          type="button"
          onClick={() => setMobileFiltersOpen((v) => !v)}
          className="md:hidden bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 w-full"
        >
          <SlidersHorizontal size={16} />
          <span>
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1">· {activeFilterCount}</span>
            )}
          </span>
        </button>
      </header>

      {/* NL search — writes params_used into the URL wholesale. A fresh
          translation REPLACES the current filters rather than merging: the
          sentence is the coach's whole intent, and merging leftovers from a
          previous manual filter into it produces searches nobody asked for. */}
      <AiSearchBar
        onApply={(params) => {
          const next = new URLSearchParams()
          for (const [key, value] of Object.entries(params)) {
            next.set(key, value)
          }
          setSearchParams(next)
        }}
      />

      <div className="grid grid-cols-1 md:grid-cols-[224px_1fr] gap-8 border-t border-hoop-border pt-5">
        <aside className={mobileFiltersOpen ? 'block' : 'hidden md:block'}>
          <FilterSidebar
            filters={filters}
            updateFilter={updateFilter}
            hasActiveFilters={hasActiveFilters}
            clearAll={clearAll}
            countries={countries}
            leagues={leagues}
          />
        </aside>

        <section>
          {err && (
            <div className="border border-hoop-red-bg bg-hoop-red-bg text-hoop-red rounded-md px-4 py-3 text-sm mb-4">
              {err}
            </div>
          )}

          {loading ? (
            <PlayerCardGridSkeleton />
          ) : results.length === 0 ? (
            <EmptyState
              hasFilters={hasActiveFilters}
              onClear={clearAll}
              fibaOnly={filters.fiba_only}
              // updateFilter deletes just this key from a copy of the
              // current searchParams, so every other active filter
              // survives the click.
              onClearFiba={() => updateFilter('fiba_only', false)}
              hasOtherFilters={activeFilterCount > 1}
              heightFilterActive={heightFilterActive}
              onClearHeight={clearHeightFilters}
              hasOtherThanHeight={hasOtherThanHeight}
            />
          ) : (
            <>
              {/* Exclusion caption — the non-empty counterpart to the height
                  branch of EmptyState. A coach who filters 190cm+ and gets one
                  player needs the same explanation as one who gets none, and
                  that path never renders an empty state. Styling mirrors the
                  low-volume caption in StatFiltersBlock. */}
              {heightFilterActive && (
                <p className="text-[11px] text-hoop-text-3 mb-2">
                  Height filters exclude players with no recorded height.
                </p>
              )}
              {/* UIR-1 active filters + count row */}
              <div className="flex items-center justify-between mb-4.5 flex-wrap gap-2.5">
                <div className="flex items-center gap-2 text-[13px] text-hoop-text-2 flex-wrap">
                  <span className="font-semibold text-hoop-text">
                    {results.length}
                    {hasMore ? '+' : ''} player{results.length === 1 ? '' : 's'}
                  </span>
                  {hasActiveFilters && (
                    <>
                      <span className="text-hoop-text-3">·</span>
                      {filters.position && (
                        <ActiveFilterChip
                          label={filters.position}
                          onRemove={() => updateFilter('position', '')}
                        />
                      )}
                      {filters.nationality && (
                        <ActiveFilterChip
                          label={filters.nationality}
                          onRemove={() => updateFilter('nationality', '')}
                        />
                      )}
                      {filters.league && (
                        <ActiveFilterChip
                          label={
                            leagues.find((l) => l.slug === filters.league)?.name ?? filters.league
                          }
                          onRemove={() => updateFilter('league', '')}
                        />
                      )}
                      {filters.league_tier && (
                        <ActiveFilterChip
                          label={TIER_LABEL[filters.league_tier]}
                          onRemove={() => updateFilter('league_tier', '')}
                        />
                      )}
                      {filters.availability_status && (
                        <ActiveFilterChip
                          label={AVAILABILITY_LABEL[filters.availability_status]}
                          onRemove={() => updateFilter('availability_status', '')}
                        />
                      )}
                      {filters.source && (
                        <ActiveFilterChip
                          label={SOURCE_OPTIONS.find((o) => o.value === filters.source)?.label ?? filters.source}
                          onRemove={() => updateFilter('source', '')}
                        />
                      )}
                      {filters.verified_only && (
                        <ActiveFilterChip
                          label="Verified"
                          onRemove={() => updateFilter('verified_only', false)}
                        />
                      )}
                      {filters.is_3x3 && (
                        <ActiveFilterChip
                          label="3x3"
                          onRemove={() => updateFilter('is_3x3', false)}
                        />
                      )}
                      {filters.fiba_only && (
                        <ActiveFilterChip
                          label="FIBA licensed"
                          onRemove={() => updateFilter('fiba_only', false)}
                        />
                      )}
                      {STAT_FILTER_DEFS.map((def) =>
                        filters[def.key] ? (
                          <ActiveFilterChip
                            key={def.key}
                            label={`${def.label} ≥ ${filters[def.key]}`}
                            onRemove={() => updateFilter(def.key, '')}
                          />
                        ) : null,
                      )}
                      {(filters.min_height_cm || filters.max_height_cm) && (
                        <ActiveFilterChip
                          label={`Height: ${filters.min_height_cm || '—'}–${filters.max_height_cm || '—'} cm`}
                          // Was two sequential updateFilter calls, which left
                          // min_height_cm in the URL: both rebuilt `next` from
                          // the same render's searchParams, so the second write
                          // clobbered the first. Same helper the empty-state
                          // control uses.
                          onRemove={clearHeightFilters}
                        />
                      )}
                      {(filters.min_age || filters.max_age) && (
                        <ActiveFilterChip
                          label={`Age: ${filters.min_age || '—'}–${filters.max_age || '—'}`}
                          onRemove={clearAgeFilters}
                        />
                      )}
                      <button
                        type="button"
                        onClick={clearAll}
                        className="text-[12.5px] font-semibold text-hoop-accent hover:underline"
                      >
                        Clear all
                      </button>
                    </>
                  )}
                </div>
              </div>
              {/* UIR-1 grid: 3 cols desktop, 2 tablet, 1 mobile, 14px gap */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
                {results.map((r) => (
                  <PlayerCard key={r.id} player={r} />
                ))}
              </div>
              {hasMore && (
                <div className="mt-6 text-center">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="bg-hoop-primary text-hoop-primary-fg px-6 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
                  >
                    {loadingMore ? 'Loading…' : 'Load more'}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {/* Save-search modal — name the current URL filter set. */}
      {saveOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-hoop-surface border border-hoop-border rounded-lg shadow-xl max-w-sm w-full p-6">
            <h2 className="text-lg font-semibold text-hoop-text mb-1">
              Save this search
            </h2>
            <p className="text-xs text-hoop-text-3 mb-4">
              Saves the current filters. Re-run it any time from your
              dashboard and see how many new players match.
            </p>
            <input
              type="text"
              autoFocus
              maxLength={60}
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void submitSaveSearch()
              }}
              placeholder="e.g. Tall PGs shooting 35%+"
              className="w-full bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text placeholder:text-hoop-text-3 mb-3"
            />
            {saveErr && (
              <p className="text-xs text-hoop-red mb-3">{saveErr}</p>
            )}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setSaveOpen(false)}
                className="text-sm text-hoop-text-2 hover:text-hoop-text"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving || !saveName.trim()}
                onClick={() => void submitSaveSearch()}
                className="bg-hoop-primary text-hoop-primary-fg px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 disabled:opacity-40"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  )
}

// UIR-1 — active filter chip (removable)
function ActiveFilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-hoop-accent-light text-hoop-accent rounded-md px-2 py-1 text-xs font-semibold">
      {label}
      <button
        type="button"
        onClick={onRemove}
        className="hover:opacity-70"
        aria-label={`Remove ${label} filter`}
      >
        ✕
      </button>
    </span>
  )
}

function FilterSidebar({
  filters,
  updateFilter,
  countries,
  leagues,
}: {
  filters: FilterState
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void
  hasActiveFilters: boolean
  clearAll: () => void
  countries: string[]
  leagues: Array<{
    slug: string
    name: string
    country: string | null
    player_count: number
  }>
}) {
  // UIR-1 removed the header row with Filters/Clear all from the sidebar
  return (
    <div className="md:border-r border-hoop-border md:pr-6 py-6 space-y-5 text-sm">
      <FilterBlock label="Position">
        <div className="flex flex-wrap gap-1.5">
          <Chip
            active={!filters.position}
            onClick={() => updateFilter('position', '')}
          >
            Any
          </Chip>
          {POSITIONS.map((p) => (
            <Chip
              key={p}
              active={filters.position === p}
              onClick={() => updateFilter('position', p)}
            >
              {p}
            </Chip>
          ))}
          {/* Tier-1 content batch — 3x3 chip alongside the position chips.
              Independent toggle (players.is_3x3_active), NOT a position:
              it AND-combines with a selected position and is untouched by
              the "Any" chip above. */}
          <Chip
            active={filters.is_3x3}
            onClick={() => updateFilter('is_3x3', !filters.is_3x3)}
          >
            3x3
          </Chip>
        </div>
      </FilterBlock>

      <div className="border-t border-hoop-border pt-4">
        <FilterBlock label="Country" htmlFor="filter-country">
        {/*
          M5.2 — replaced the free-text "ISO code" input. The bulk
          discovery import populated `players.nationality` with full
          country names (e.g. "Serbia"), not ISO codes, so the old
          input was effectively useless on the new corpus. We render
          whatever DISTINCT values live in the column today —
          /api/players/countries handles the sort + dedup.
        */}
        <select
          id="filter-country"
          value={filters.nationality}
          onChange={(e) => updateFilter('nationality', e.target.value)}
          className="w-full bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text-2"
        >
          <option value="">Any</option>
          {countries.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </FilterBlock>
      </div>

      <FilterBlock label="League" htmlFor="filter-league">
        {/*
          M6.2 — players' current league via team_leagues. Sorted by
          player_count desc per /api/players/leagues; counts shown in
          the dropdown labels so coaches can compare at a glance
          ("ABA League (493)" outweighs "Bulgarian Cup (4)" without
          having to know basketball politics).

          M6.2-followup — disambiguateLeagueName suffixes the country
          when two leagues share a display name (API-Sports has two
          "Super Cup" rows for Turkey + Greece). Programmatic over
          hardcoded so future imports are self-correcting.
        */}
        <select
          id="filter-league"
          value={filters.league}
          onChange={(e) => updateFilter('league', e.target.value)}
          className="w-full bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text-2"
        >
          <option value="">Any</option>
          {leagues.map((l) => (
            <option key={l.slug} value={l.slug}>
              {disambiguateLeagueName(l, leagues)} ({l.player_count})
            </option>
          ))}
        </select>
      </FilterBlock>

      <FilterBlock label="Representation" htmlFor="filter-representation">
        {/* M5.2 — provenance filter. Maps to players.source. */}
        <select
          id="filter-representation"
          value={filters.source}
          onChange={(e) => updateFilter('source', e.target.value as SourceFilter)}
          className="w-full bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text-2"
        >
          {SOURCE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </FilterBlock>

      <FilterBlock label="Height (cm)">
        <div className="flex gap-2 min-w-0">
          <NumberInput
            value={filters.min_height_cm}
            onChange={(v) => updateFilter('min_height_cm', v)}
            placeholder="Min"
            ariaLabel="Minimum height in cm"
          />
          <NumberInput
            value={filters.max_height_cm}
            onChange={(v) => updateFilter('max_height_cm', v)}
            placeholder="Max"
            ariaLabel="Maximum height in cm"
          />
        </div>
      </FilterBlock>

      <FilterBlock label="Age">
        <div className="flex gap-2 min-w-0">
          <NumberInput
            value={filters.min_age}
            onChange={(v) => updateFilter('min_age', v)}
            placeholder="Min"
            ariaLabel="Minimum age"
          />
          <NumberInput
            value={filters.max_age}
            onChange={(v) => updateFilter('max_age', v)}
            placeholder="Max"
            ariaLabel="Maximum age"
          />
        </div>
      </FilterBlock>

      <StatFiltersBlock filters={filters} updateFilter={updateFilter} />

      <FilterBlock label="Target league" htmlFor="filter-target-league">
        <select
          id="filter-target-league"
          value={filters.league_tier}
          onChange={(e) => updateFilter('league_tier', e.target.value as LeagueTier | '')}
          className="w-full bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text-2"
        >
          <option value="">Any</option>
          {TIERS.map((t) => (
            <option key={t} value={t}>
              {TIER_LABEL[t]}
            </option>
          ))}
        </select>
      </FilterBlock>

      <FilterBlock label="Availability" htmlFor="filter-availability">
        <select
          id="filter-availability"
          value={filters.availability_status}
          onChange={(e) =>
            updateFilter('availability_status', e.target.value as AvailabilityStatus | '')
          }
          className="w-full bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text-2"
        >
          <option value="">Any</option>
          {AVAILABILITY.map((a) => (
            <option key={a} value={a}>
              {AVAILABILITY_LABEL[a]}
            </option>
          ))}
        </select>
      </FilterBlock>

      <label className="flex items-center gap-2 cursor-pointer text-hoop-text-2">
        <input
          type="checkbox"
          checked={filters.verified_only}
          onChange={(e) => updateFilter('verified_only', e.target.checked)}
          className="rounded border-hoop-border accent-hoop-primary"
        />
        <span>Verified profiles only</span>
      </label>

      <label className="flex items-center gap-2 cursor-pointer text-hoop-text-2">
        <input
          type="checkbox"
          checked={filters.fiba_only}
          onChange={(e) => updateFilter('fiba_only', e.target.checked)}
          className="rounded border-hoop-border accent-hoop-primary"
        />
        <span>FIBA licensed agent</span>
      </label>
    </div>
  )
}

// Stat-threshold filters (2026-07-25) — collapsible "Stats — latest
// season" section. Min-only inputs (accepted deviation from the
// Height/Age Min/Max pattern: coach intent is "at least X"). Values
// round-trip through the URL like every other Discover filter. Defaults
// open when any stat filter is already active (e.g. a shared link).
function StatFiltersBlock({
  filters,
  updateFilter,
}: {
  filters: FilterState
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void
}) {
  const anyActive = STAT_FILTER_DEFS.some((def) => !!filters[def.key])
  const [open, setOpen] = useState(anyActive)
  const labelClass =
    'block text-[11px] font-bold uppercase tracking-[0.14em] text-hoop-text-3'

  return (
    <div className="border-t border-hoop-border pt-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 mb-2.5"
      >
        <span className={labelClass}>Stats — latest season</span>
        <ChevronDown
          className={
            'w-3.5 h-3.5 text-hoop-text-3 transition-transform ' +
            (open ? 'rotate-180' : '')
          }
        />
      </button>
      {open && (
        <div>
          <p className="text-[11px] text-hoop-text-3 mb-2.5">
            Matched against each player's most recent season of official
            league data.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {STAT_FILTER_DEFS.map((def) => (
              <label key={def.key} className="block min-w-0">
                <span className="block text-[11px] text-hoop-text-3 mb-1">
                  {def.label}
                </span>
                <input
                  type="number"
                  inputMode="numeric"
                  min={0}
                  aria-label={`Minimum ${def.label}`}
                  value={filters[def.key]}
                  onChange={(e) =>
                    updateFilter(def.key as StatFilterKey, e.target.value)
                  }
                  placeholder="min"
                  className="w-full min-w-0 bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text placeholder:text-hoop-text-3"
                />
              </label>
            ))}
          </div>
          {/* Volume rider (2026-07-26) — UX caution only; the query has
              no attempt-volume guard by design. */}
          <p className="mt-2 text-[11px] text-hoop-text-3">
            Percentages from low-attempt seasons can be inflated.
          </p>
        </div>
      )}
    </div>
  )
}

function FilterBlock({
  label,
  children,
  htmlFor,
}: {
  label: string
  children: React.ReactNode
  // Single-control blocks pass htmlFor so the <label> binds to the control.
  // Multi-control blocks (position chips, min/max pairs) omit it and instead
  // get a role=group + aria-labelledby association.
  htmlFor?: string
}) {
  // UIR-1 — uppercase 11px tracked micro-labels per group
  const labelClass =
    'block text-[11px] font-bold uppercase tracking-[0.14em] text-hoop-text-3 mb-2.5'
  if (htmlFor) {
    return (
      <div>
        <label htmlFor={htmlFor} className={labelClass}>
          {label}
        </label>
        {children}
      </div>
    )
  }
  const labelId =
    'filter-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
  return (
    <div role="group" aria-labelledby={labelId}>
      <div id={labelId} className={labelClass}>
        {label}
      </div>
      {children}
    </div>
  )
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  // UIR-1 — chip toggles: navy-filled active, white bordered inactive
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'px-[11px] py-[5px] rounded-md text-[12.5px] font-semibold transition-colors cursor-pointer ' +
        (active
          ? 'bg-hoop-text text-hoop-bg'
          : 'bg-white dark:bg-hoop-surface text-hoop-text-2 border border-hoop-border hover:border-hoop-accent')
      }
    >
      {children}
    </button>
  )
}

function NumberInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
}: {
  value: string
  onChange: (v: string) => void
  placeholder: string
  // The visible group label ("Height (cm)" / "Age") only names the pair; each
  // input still needs its own accessible name so SR users know min vs max.
  ariaLabel: string
}) {
  return (
    <input
      type="number"
      inputMode="numeric"
      aria-label={ariaLabel}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="flex-1 min-w-0 bg-white dark:bg-hoop-surface border border-hoop-border rounded-[7px] px-[11px] py-2 text-[13px] text-hoop-text placeholder:text-hoop-text-3"
    />
  )
}

function EmptyState({
  hasFilters,
  onClear,
  fibaOnly,
  onClearFiba,
  hasOtherFilters,
  heightFilterActive,
  onClearHeight,
  hasOtherThanHeight,
}: {
  hasFilters: boolean
  onClear: () => void
  // FIBA branch (2026-07-29). The filter is data-starved at launch — few
  // agents have registered a license — so a zero-result FIBA search is the
  // common case, and the generic "No players match these filters" reads as
  // a dead end rather than a one-click-recoverable narrow filter.
  fibaOnly: boolean
  onClearFiba: () => void
  // Whether anything OTHER than fiba_only is narrowing the search. Drives
  // whether we also offer Clear all — with fiba_only as the only active
  // filter, Clear all and Clear FIBA filter would do the same thing.
  hasOtherFilters: boolean
  // Height branch (2026-07-29). Higher impact than the FIBA branch: height is
  // the first filter a basketball coach reaches for, and it silently drops
  // every player with no recorded height.
  heightFilterActive: boolean
  onClearHeight: () => void
  // Same role as hasOtherFilters, but nets out BOTH height keys.
  hasOtherThanHeight: boolean
}) {
  if (fibaOnly) {
    return (
      <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center">
        <p className="text-hoop-text text-sm font-medium">
          No players match the FIBA licensed agent filter.
        </p>
        {/*
          Deliberately scoped to the filter, NOT to the system. The client
          never queried how many agents hold a license, and "no agents have
          registered one yet" goes stale the moment one does while some
          other filter is what actually zeroed the list.
        */}
        <p className="text-hoop-text-3 text-[13px] mt-2 max-w-md mx-auto">
          This filter matches only players whose representing agent has
          registered a FIBA license number.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={onClearFiba}
            className="text-sm font-semibold text-hoop-accent hover:underline"
          >
            Clear FIBA filter
          </button>
          {hasOtherFilters && (
            <button
              type="button"
              onClick={onClear}
              className="text-sm text-hoop-text-2 hover:text-hoop-text underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>
    )
  }

  // Ordered AFTER the FIBA branch deliberately: when both filters are active
  // the FIBA copy wins. Composing a combined message would be worse than
  // picking one — clearing either filter re-renders and surfaces the other.
  if (heightFilterActive) {
    return (
      <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center">
        <p className="text-hoop-text text-sm font-medium">
          No players match this height range.
        </p>
        {/*
          The second clause is the useful half: it tells a coach the empty
          result is about the recorded data, not a malformed query. No count
          appears here — the client never queried how many players have a
          height, and the FIBA branch holds the same line.
        */}
        <p className="text-hoop-text-3 text-[13px] mt-2 max-w-md mx-auto">
          Height filters exclude players with no recorded height, so a range can
          come back empty even when players would otherwise fit it.
        </p>
        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            type="button"
            onClick={onClearHeight}
            className="text-sm font-semibold text-hoop-accent hover:underline"
          >
            Clear height filter
          </button>
          {hasOtherThanHeight && (
            <button
              type="button"
              onClick={onClear}
              className="text-sm text-hoop-text-2 hover:text-hoop-text underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="border border-dashed border-hoop-border rounded-lg p-10 text-center">
      <p className="text-hoop-text-2 text-sm">
        {hasFilters
          ? 'No players match these filters.'
          : 'No players in the system yet.'}
      </p>
      {hasFilters && (
        <button
          type="button"
          onClick={onClear}
          className="mt-3 text-sm text-hoop-text-2 hover:text-hoop-text underline"
        >
          Clear filters
        </button>
      )}
    </div>
  )
}
